/** Compare Mongo/ObjectId/string ids from API responses. */
export function normalizePricingId(id) {
  if (id == null) return "";
  if (typeof id === "object" && id._id != null) return String(id._id);
  return String(id);
}

export function findPricingRowIndex(rows, storeId, productId) {
  const sid = normalizePricingId(storeId);
  const pid = normalizePricingId(productId);
  return rows.findIndex(
    (option) =>
      option?.merchantStore != null &&
      option?.product != null &&
      normalizePricingId(option.merchantStore) === sid &&
      normalizePricingId(option.product) === pid
  );
}

export function isMissingPriceValue(price) {
  return price === undefined || price === null || price === "";
}

export function variantsObjectHasAny(variants, { allowZero = false } = {}) {
  const hasVariantValue = (value) => {
    if (value == null || value === "") return false;
    const n = parseFloat(value);
    if (Number.isNaN(n)) return false;
    return allowZero ? n >= 0 : n > 0;
  };
  return (
    hasVariantValue(variants?.sedan) ||
    hasVariantValue(variants?.wagon) ||
    hasVariantValue(variants?.["4wd"])
  );
}

/**
 * Include price in API payload only when the user edited price or we are creating
 * a row with a meaningful price. Admin-fee-only saves omit price so the backend
 * preserves the stored contract rate (including $0 rows).
 */
export function shouldSendPriceInRequest({
  hasExistingRow,
  price,
  priceFieldEdited = false,
}) {
  if (priceFieldEdited) return true;
  if (!hasExistingRow) return true;
  if (isMissingPriceValue(price)) return false;
  const numericPrice = parseFloat(price);
  if (Number.isNaN(numericPrice)) return false;
  return numericPrice > 0;
}

/** Omit vehicle tier fields unless the user edited tiers or "Use tiers". */
export function shouldSendVehicleTierFieldsInRequest({
  hasExistingRow,
  variantsFieldEdited = false,
  useTierFieldEdited = false,
  vehicleTypeVariants,
}) {
  if (variantsFieldEdited || useTierFieldEdited) return true;
  if (!hasExistingRow && variantsObjectHasAny(vehicleTypeVariants)) return true;
  return false;
}

/**
 * Whether a pricing row should be written to the API (never skip admin-fee saves).
 */
export function shouldPersistPricingRow({
  price,
  vehicleTypeVariants,
  useVehicleVariantPricing,
  hasExistingRow,
  adminFee,
  variantsFieldEdited = false,
}) {
  const hasTierAmounts = variantsObjectHasAny(vehicleTypeVariants, {
    allowZero: variantsFieldEdited,
  });
  const adminFeeNumber = parseFloat(adminFee);
  const hasAdminFee =
    adminFee != null && adminFee !== "" && !Number.isNaN(adminFeeNumber);

  if (hasExistingRow || hasAdminFee || hasTierAmounts || useVehicleVariantPricing) {
    return true;
  }

  if (isMissingPriceValue(price)) {
    return false;
  }
  const numericPrice = parseFloat(price);
  return !Number.isNaN(numericPrice);
}

/** Row deletion from the grid is not allowed — pricing rows are never auto-deleted. */
export function shouldDeletePricingRow() {
  return false;
}
